// The PDF renderer's way in — one hook, for every report.
//
// TWO separate credentials, doing two different jobs:
//
//   __render_token  says WHICH report may be drawn. Single-use, 60s, bound to
//                   this exact URL (mig 20260816100000). Redeemed FIRST, so a
//                   replayed or mis-bound render never reaches any data.
//   __render_otp    says AS WHOM. A single-use magic-link hash that verifyOtp
//                   trades for the sending operator's own session.
//
// ⚠️ Why not just mint a JWT: this project signs user tokens with ES256 and
// publishes a JWKS whose key_ops are ["verify"] — the private key is
// Supabase's, not ours. So Supabase does the signing and we carry the hash.
//
// The payoff is that a render route then reads EXACTLY as the operator's own
// screen does — same queries, same RLS, same storage-signed evidence photos. A
// SQL DEFINER function per report could return rows but could never sign a
// storage URL, which would have reached growers as broken photo boxes on a QC
// report.
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { exposeRenderSignOut } from '@/lib/renderSignOut'

export type RenderSessionState = 'pending' | 'ready' | 'failed'

/** Why the render session failed, coarse enough to be safe in a log.
 *  Published on `document.documentElement.dataset.renderFail` so the PDF
 *  renderer can put it in its 422 and send-report can turn it into a sentence
 *  the operator reads. Never a credential, never a stack. */
type RenderFailReason = 'no-token' | 'no-otp' | 'redeem-refused' | 'signin-refused'

function publishFail(reason: RenderFailReason): void {
  document.documentElement.dataset.renderFail = reason
}

/** `source` is injectable so the parsing can be tested without a document —
 *  it is on the auth path, and reading a NEIGHBOURING cookie's value would
 *  hand garbage to the redeem RPC. */
export function readCookie(name: string, source?: string): string | null {
  const raw = source ?? (typeof document === 'undefined' ? null : document.cookie)
  if (raw == null) return null
  for (const part of raw.split(';')) {
    const [k, ...v] = part.trim().split('=')
    if (k === name) return decodeURIComponent(v.join('=')) || null
  }
  return null
}

/**
 * Establishes the render session. Returns `pending` until it resolves, then
 * `ready` or `failed` — a report should publish `error` to the renderer on
 * `failed` so the send is refused rather than a blank document produced.
 */
export function useRenderSession(): RenderSessionState {
  const [state, setState] = useState<RenderSessionState>('pending')

  useEffect(() => {
    let cancelled = false
    const token = readCookie('__render_token')
    const otp   = readCookie('__render_otp')
    if (!token) { publishFail('no-token'); setState('failed'); return }
    if (!otp)   { publishFail('no-otp');   setState('failed'); return }

    ;(async () => {
      try {
        // 1. Redeem the render token FIRST — before any identity exists. This
        //    is what makes a replayed or URL-mismatched render fail closed,
        //    and it is deliberately not merged into step 2: a valid session
        //    must not be enough on its own to draw an arbitrary report.
        // ⚠️ `p_report_url`, NOT `p_url`. PostgREST resolves an RPC by its
        //    ARGUMENT NAMES: one wrong name is a 404 (PGRST202), not a type
        //    error, so nothing catches it until a real render fails.
        const { error: redeemErr } = await supabase.rpc('redeem_render_token', {
          p_token:      token,
          p_report_url: window.location.href,
        })
        if (redeemErr) { publishFail('redeem-refused'); throw redeemErr }

        // 2. Trade the hash for the operator's session.
        const { error: otpErr } = await supabase.auth.verifyOtp({
          token_hash: otp, type: 'magiclink',
        })
        if (otpErr) { publishFail('signin-refused'); throw otpErr }

        // The renderer's way out: it revokes this session (scope: 'local')
        // once the PDF is printed — see src/lib/renderSignOut.ts.
        exposeRenderSignOut(supabase)
        if (!cancelled) setState('ready')
      } catch {
        // The COARSE reason is published above; no detail beyond it. This runs
        // in a browser whose logs we do not control, and the inputs are
        // credentials. Four failures on this path were each diagnosed by
        // guesswork because nothing came back — a four-value enum ends that
        // without leaking anything.
        if (!cancelled) setState('failed')
      }
    })()

    // ⚠️ NO signOut in this cleanup. React StrictMode double-invokes effects
    // (mount → cleanup → mount), so a sign-out here destroys the session that
    // was just established and the page never becomes ready — caught in the
    // render harness, where the route sat at reportState=undefined with a
    // /auth/v1/logout in the request log.
    //
    // The session is ended by the RENDERER instead: right after page.pdf() it
    // calls the sign-out this hook exposes (scope: 'local', so only this
    // session's refresh token is revoked on the server), then closes the
    // browser. It never touches disk we keep, and the magic-link hash it came
    // from is single-use and already spent. (Closing the browser alone left the
    // refresh token valid — security review of #2897, L1.)
    return () => { cancelled = true }
  }, [])

  return state
}
